import { hash } from 'object-code';
import { Minimatch } from 'minimatch';
import { useState, useEffect, useRef } from 'react';
import { DateTime, Duration } from 'luxon';

var __defProp$3 = Object.defineProperty;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$3 = (obj, key, value) => {
  __defNormalProp$3(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const _Store = class _Store {
  constructor() {
    __publicField$3(this, "entries", /* @__PURE__ */ new Map());
  }
  getOrSet(id, resourceBuilder, options = {}) {
    const { tags = [] } = options;
    const existing = this.entries.get(id);
    if (existing) {
      return existing.resource;
    }
    const newResource = resourceBuilder();
    this.entries.set(id, {
      resource: newResource,
      tags
    });
    return newResource;
  }
  get(id) {
    return this.entries.get(id)?.resource;
  }
  findByError(error) {
    return this.getAll().filter((resource) => resource.isMatchingError(error));
  }
  getAll(tag) {
    const entriesArray = Array.from(this.entries.values());
    if (tag === void 0) {
      return entriesArray.map((e) => e.resource);
    }
    const mm = new Minimatch(tag);
    const testSomeTagsMatchingPattern = (tags) => tags.some((t) => mm.match(t));
    return entriesArray.filter((e) => testSomeTagsMatchingPattern(e.tags)).map((e) => e.resource);
  }
  clear() {
    this.entries.clear();
  }
};
__publicField$3(_Store, "default", new _Store());
let Store = _Store;

const defaultStorageKeyBuilder = (input) => {
  return hash(input).toString();
};

const setValue = (value) => Object.freeze({
  isSet: true,
  value
});
const emptyValue = Object.freeze({
  isSet: false
});

var __defProp$2 = Object.defineProperty;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$2 = (obj, key, value) => {
  __defNormalProp$2(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class ObservableValue {
  constructor(value) {
    __publicField$2(this, "value");
    // using unknown since otherwise the ObservableValue seems to be invariant on type T
    __publicField$2(this, "observers", /* @__PURE__ */ new Set());
    this.value = value;
  }
  notifyObservers(newValue) {
    this.observers.forEach((o) => o(newValue));
  }
  get observerCount() {
    return this.observers.size;
  }
  observe(observer) {
    this.observers.add(observer);
    return () => this.observers.delete(observer);
  }
  updateValue(newValue) {
    if (this.value === newValue) {
      return;
    }
    this.value = newValue;
    this.notifyObservers(newValue);
  }
}

const useWatchObservableValue = (observable) => {
  const [watchedValue, setWatchedValue] = useState(observable.value);
  useEffect(() => {
    setWatchedValue(observable.value);
    return observable.observe(setWatchedValue);
  }, [observable]);
  return watchedValue;
};

const useOnWindowFocused = (cb, deps) => {
  useEffect(() => {
    window.addEventListener("focus", cb);
    return () => {
      window.removeEventListener("focus", cb);
    };
  }, deps);
};

const useWatchResourceValue = (resource, options = {}) => {
  const {
    keepValueWhileLoading = true,
    useSuspense = true,
    refreshOnWindowFocus = false,
    autoRefresh
  } = options;
  const observedValue = useWatchObservableValue(resource.value);
  const error = useWatchObservableValue(resource.error);
  const previousValue = useRef(observedValue);
  useEffect(() => {
    if (autoRefresh) {
      return resource.addTTL(autoRefresh);
    }
  }, [resource, hash(autoRefresh)]);
  useOnWindowFocused(() => {
    if (refreshOnWindowFocus) {
      resource.refresh();
    }
  }, [resource, refreshOnWindowFocus]);
  void resource.load();
  if (observedValue.isSet) {
    previousValue.current = observedValue;
    if (useSuspense) {
      return observedValue.value;
    }
    return Object.freeze({
      maybeValue: observedValue.value,
      value: observedValue.value,
      hasValue: true,
      isLoading: false
    });
  }
  if (keepValueWhileLoading && previousValue.current.isSet) {
    if (useSuspense) {
      return previousValue.current.value;
    }
    return Object.freeze({
      maybeValue: previousValue.current.value,
      value: previousValue.current.value,
      hasValue: true,
      isLoading: true
    });
  }
  if (error.isSet) {
    throw error.value;
  }
  if (useSuspense) {
    throw resource.loaderPromise;
  }
  return Object.freeze({
    maybeValue: void 0,
    hasValue: false,
    isLoading: true
  });
};

var __defProp$1 = Object.defineProperty;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$1 = (obj, key, value) => {
  __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class ConsolidatedTimeout {
  constructor(callback) {
    __publicField$1(this, "callback");
    __publicField$1(this, "startTime");
    __publicField$1(this, "timeoutMillis", /* @__PURE__ */ new Set());
    __publicField$1(this, "runningTimeout");
    this.startTime = DateTime.now();
    this.callback = callback;
  }
  start() {
    this.startTime = DateTime.now();
    this.startNextTimeout();
  }
  clear() {
    if (this.runningTimeout) {
      clearTimeout(this.runningTimeout);
      this.runningTimeout = void 0;
    }
  }
  addTimeout(timeout) {
    const timeoutMs = Duration.fromDurationLike(timeout).toMillis();
    this.timeoutMillis.add(timeoutMs);
    this.startNextTimeout();
    return () => {
      this.timeoutMillis.delete(timeoutMs);
      this.startNextTimeout();
    };
  }
  startNextTimeout() {
    this.clear();
    if (this.timeoutMillis.size === 0) {
      return;
    }
    const shortestTimeout = Math.min(...this.timeoutMillis);
    const elapsedTime = this.startTime.diffNow().negate().toMillis();
    const ms = shortestTimeout - elapsedTime;
    if (ms <= 0) {
      this.callback();
    } else {
      this.runningTimeout = setTimeout(() => this.callback(), ms);
    }
  }
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class AsyncResource {
  constructor(loader) {
    __publicField(this, "loader");
    __publicField(this, "loaderPromise");
    __publicField(this, "loaderPromiseVersion", 0);
    __publicField(this, "autoRefreshTimeout");
    __publicField(this, "value", new ObservableValue(emptyValue));
    __publicField(this, "error", new ObservableValue(emptyValue));
    __publicField(this, "state", new ObservableValue("void"));
    this.loader = loader;
    this.autoRefreshTimeout = new ConsolidatedTimeout(() => this.refresh());
  }
  refresh() {
    this.loaderPromiseVersion++;
    this.loaderPromise = void 0;
    this.value.updateValue(emptyValue);
    this.error.updateValue(emptyValue);
    this.state.updateValue("void");
  }
  addTTL(ttl) {
    return this.autoRefreshTimeout.addTimeout(ttl);
  }
  async load() {
    if (this.value.value.isSet || this.error.value.isSet) {
      return;
    }
    if (this.loaderPromise === void 0) {
      this.loaderPromise = this.handleLoading();
    }
    return this.loaderPromise;
  }
  isMatchingError(error) {
    if (!this.error.value.isSet) {
      return false;
    }
    return error === true || this.error.value.value === error;
  }
  async handleLoading() {
    const loaderPromiseVersion = ++this.loaderPromiseVersion;
    let result = emptyValue;
    let error = emptyValue;
    this.state.updateValue("loading");
    try {
      const awaitedResult = await this.loader();
      result = setValue(awaitedResult);
    } catch (e) {
      error = setValue(e);
    }
    if (this.loaderPromiseVersion === loaderPromiseVersion) {
      this.loaderPromise = void 0;
      if (result.isSet) {
        this.value.updateValue(result);
        this.state.updateValue("loaded");
      } else if (error.isSet) {
        this.error.updateValue(error);
        this.state.updateValue("error");
      }
    }
    this.autoRefreshTimeout.start();
  }
  use(options = {}) {
    return useWatchResourceValue(this, options);
  }
  /** @deprecated Renamed to `use` in version 2 */
  watch(options = {}) {
    return this.use(options);
  }
  watchState() {
    return useWatchObservableValue(this.state);
  }
}

const emptyResource = new AsyncResource(
  () => Promise.resolve(void 0)
);
function getAsyncResource(asyncFn, parameters, options = {}) {
  const { loaderId, tags } = options;
  if (parameters === null) {
    return emptyResource;
  }
  const storageKey = defaultStorageKeyBuilder({
    asyncFn,
    parameters,
    loaderId
  });
  const asyncResourceLoader = () => asyncFn(...parameters);
  const resourceBuilder = () => new AsyncResource(asyncResourceLoader);
  return Store.default.getOrSet(storageKey, resourceBuilder, {
    tags
  });
}

export { AsyncResource as A, Store as S, getAsyncResource as g };
